import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Pencil, Trash2, Users, MapPin, BarChart3, Coffee, Wine, Beer, Martini, DollarSign, Building2, Check, X, KeyRound, Copy, Sparkles, AlertTriangle } from 'lucide-react';
import { CATEGORY_META, CATEGORY_ORDER } from '@/lib/utils';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { AddressAutocompleteInput } from '@/components/AddressAutocompleteInput';

const BLANK_VENUE = {
  name: '', category: 'bar', address: '', city: '', state: '',
  lat: 0, lon: 0, hours: '', phone: '', description: '', image_url: '',
  discount_text: '', is_21_plus: true,
};

function InactiveUserCleanupPanel() {
  const [hours, setHours] = useState(24);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmingText, setConfirmingText] = useState('');
  const [executing, setExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runPreview = async () => {
    setLoadingPreview(true);
    setLastResult(null);
    try {
      const r = await api.post('/admin/cleanup-inactive-users', {
        dry_run: true,
        older_than_hours: Number(hours) || 24,
      });
      setPreview(r.data);
      setConfirmingText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const runExecute = async () => {
    if (confirmingText.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setExecuting(true);
    try {
      const r = await api.post('/admin/cleanup-inactive-users', {
        dry_run: false,
        older_than_hours: Number(hours) || 24,
      });
      setLastResult(r.data);
      setPreview(null);
      setConfirmingText('');
      toast.success(`Deleted ${r.data.deleted_count} inactive users`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-cleanup-panel">
      <GlassCard className="p-4 border-yellow-400/25 bg-yellow-400/[0.04]">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-300" />
          <div className="space-y-1">
            <p className="font-semibold text-white">Inactive user cleanup</p>
            <p className="text-sm text-white/70">
              Permanently remove user accounts that have <strong>never engaged</strong> with the app
              (no posts, stories, check-ins, or venue claims) and were created more than <em>N</em> hours
              ago. Protected accounts (admin, demo, owner, venue owners, users with claims) are always
              excluded. This action cascade-deletes the user and all of their associated data. It is
              <strong> irreversible</strong>.
            </p>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-32">
            <Label className="text-white/80">Older than (hours)</Label>
            <Input
              type="number"
              min={1}
              max={8760}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-1 h-10 w-32 bg-white/5 border-white/12 text-white"
              data-testid="admin-cleanup-hours-input"
            />
          </div>
          <Button
            type="button"
            onClick={runPreview}
            disabled={loadingPreview}
            className="h-10 rounded-xl bg-white/10 text-white hover:bg-white/15 border border-white/15 disabled:opacity-60"
            data-testid="admin-cleanup-preview-button"
          >
            {loadingPreview ? 'Loading…' : 'Preview candidates'}
          </Button>
        </div>

        {preview && (
          <div className="space-y-2" data-testid="admin-cleanup-preview-result">
            <p className="text-sm text-white/80">
              <strong className="text-white">{preview.candidate_count}</strong> user
              {preview.candidate_count === 1 ? '' : 's'} would be deleted
              {preview.truncated ? ' (showing first 200)' : ''}.
            </p>
            {preview.candidate_count === 0 ? (
              <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/10 p-3 text-sm text-emerald-200">
                No inactive users match. Nothing to clean up.
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
                {preview.candidates.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 p-3 text-sm"
                    data-testid={`admin-cleanup-candidate-${u.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-white">{u.display_name}</p>
                      <p className="truncate text-xs text-white/60">
                        {u.email} · created {new Date(u.created_at).toLocaleString()}
                      </p>
                    </div>
                    <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/60">
                      {u.role}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {preview.candidate_count > 0 && (
              <div className="mt-3 space-y-2 rounded-lg border border-red-500/30 bg-red-500/[0.06] p-3">
                <p className="text-sm text-red-100">
                  Type <span className="rounded bg-red-500/25 px-1 font-mono text-red-100">DELETE</span> to
                  confirm and permanently remove these {preview.candidate_count} users:
                </p>
                <div className="flex gap-2">
                  <Input
                    value={confirmingText}
                    onChange={(e) => setConfirmingText(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    className="h-10 flex-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                    data-testid="admin-cleanup-confirm-input"
                  />
                  <Button
                    onClick={runExecute}
                    disabled={executing || confirmingText.trim().toUpperCase() !== 'DELETE'}
                    className="h-10 rounded-xl bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 font-semibold"
                    data-testid="admin-cleanup-execute-button"
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    {executing ? 'Deleting…' : 'Delete all'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {lastResult && (
          <div
            className="rounded-lg border border-emerald-400/25 bg-emerald-400/10 p-3 text-sm text-emerald-100"
            data-testid="admin-cleanup-last-result"
          >
            <p className="font-semibold">
              Deleted {lastResult.deleted_count} of {lastResult.candidate_count} candidates
            </p>
            <p className="text-xs text-emerald-100/80 mt-1">
              Cascade totals: posts={lastResult.totals?.posts_deleted ?? 0} ·
              stories={lastResult.totals?.stories_deleted ?? 0} ·
              check-ins={lastResult.totals?.checkins_deleted ?? 0} ·
              venues detached={lastResult.totals?.venues_detached ?? 0}
            </p>
          </div>
        )}
      </GlassCard>
    </div>
  );
}

export default function AdminPage() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [venues, setVenues] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payFilter, setPayFilter] = useState('all');
  const [subs, setSubs] = useState([]);
  const [claims, setClaims] = useState([]);
  const [resets, setResets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_VENUE);
  const [open, setOpen] = useState(false);
  const [seeder, setSeeder] = useState(null);
  const [seederBusy, setSeederBusy] = useState(false);
  const [linksByReq, setLinksByReq] = useState({});

  const loadAll = async () => {
    const [s, v, p, c, sd, r, sb] = await Promise.all([
      api.get('/admin/stats'),
      api.get('/venues', { params: { limit: 500 } }),
      api.get('/admin/payments').catch(() => ({ data: [] })),
      api.get('/admin/venue-claims').catch(() => ({ data: [] })),
      api.get('/admin/seeder/status').catch(() => ({ data: null })),
      api.get('/admin/password-resets').catch(() => ({ data: [] })),
      api.get('/admin/subscriptions').catch(() => ({ data: [] })),
    ]);
    setStats(s.data);
    setVenues(v.data);
    setPayments(p.data);
    setClaims(c.data);
    setSeeder(sd.data);
    setResets(r.data);
    setSubs(sb.data);
  };

  useEffect(() => { loadAll(); }, []);

  const runSeeder = async () => {
    setSeederBusy(true);
    try {
      const r = await api.post('/admin/seeder/run', null, { params: { force: true } });
      toast.success(r.data.ran ? `Created ${r.data.posts_created} posts + ${r.data.stories_created} stories` : (r.data.reason || 'Skipped'));
      loadAll();
    } catch { toast.error('Seeder run failed'); }
    finally { setSeederBusy(false); }
  };

  const cleanupSeeder = async () => {
    if (!confirm('Delete ALL fictitious users, posts, and stories? This cannot be undone.')) return;
    setSeederBusy(true);
    try {
      const r = await api.post('/admin/seeder/cleanup');
      toast.success(`Cleaned: ${r.data.users_deleted} users, ${r.data.posts_deleted} posts, ${r.data.stories_deleted} stories`);
      loadAll();
    } catch { toast.error('Cleanup failed'); }
    finally { setSeederBusy(false); }
  };

  const approvePayment = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/approve`, { notes: null });
      toast.success('Payment approved & subscription activated');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };
  const rejectPayment = async (id) => {
    const notes = prompt('Reason for rejection?') || '';
    try {
      await api.post(`/admin/payments/${id}/reject`, { notes });
      toast.success('Payment rejected');
      loadAll();
    } catch { toast.error('Reject failed'); }
  };
  const approveClaim = async (id) => {
    try {
      await api.post(`/admin/venue-claims/${id}/approve`, { notes: null });
      toast.success('Claim approved \u2014 user promoted to venue owner');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };
  const rejectClaim = async (id) => {
    const notes = prompt('Reason for rejection?') || '';
    try {
      await api.post(`/admin/venue-claims/${id}/reject`, { notes });
      toast.success('Claim rejected');
      loadAll();    } catch { toast.error('Reject failed'); }
  };

  const startCreate = () => { setEditing(null); setForm(BLANK_VENUE); setOpen(true); };
  const startEdit = (v) => { setEditing(v); setForm({ ...BLANK_VENUE, ...v }); setOpen(true); };

  // ---------- Password resets ----------
  const generateResetLink = async (reqId) => {
    try {
      const { data } = await api.post(`/admin/password-resets/${reqId}/generate`);
      const fullUrl = `${window.location.origin}${data.reset_path}`;
      setLinksByReq((prev) => ({ ...prev, [reqId]: { url: fullUrl, expires_at: data.expires_at } }));
      try { await navigator.clipboard.writeText(fullUrl); toast.success('Reset link generated & copied'); }
      catch { toast.success('Reset link generated'); }
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate link'); }
  };

  const cancelReset = async (reqId) => {
    if (!confirm('Cancel this reset request?')) return;
    try {
      await api.post(`/admin/password-resets/${reqId}/cancel`);
      toast.success('Request cancelled');
      setLinksByReq((prev) => { const n = { ...prev }; delete n[reqId]; return n; });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  const copyResetLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); }
    catch { toast.error('Copy failed'); }
  };
  const save = async () => {
    try {
      const payload = {
        ...form,
        lat: parseFloat(form.lat) || 0,
        lon: parseFloat(form.lon) || 0,
      };
      if (editing) await api.patch(`/venues/${editing.id}`, payload);
      else await api.post('/venues', payload);
      toast.success(editing ? 'Venue updated' : 'Venue created');
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const remove = async (v) => {
    if (!confirm(`Delete "${v.name}"?`)) return;
    try {
      await api.delete(`/venues/${v.id}`);
      toast.success('Venue deleted');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  const catChartData = stats ? CATEGORY_ORDER.map((c) => ({
    name: CATEGORY_META[c].label, count: stats.venues_by_category?.[c] || 0,
  })) : [];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-5xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="admin-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Admin Dashboard</h1>
          <div className="ml-auto"><Logo size={96} withText={false} /></div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-4 px-4 py-4">
        <Tabs defaultValue="stats">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="stats" data-testid="admin-tab-stats"><BarChart3 className="mr-2 h-4 w-4" /> Stats</TabsTrigger>
            <TabsTrigger value="venues" data-testid="admin-tab-venues"><MapPin className="mr-2 h-4 w-4" /> Venues</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments"><DollarSign className="mr-2 h-4 w-4" /> Payments {payments.filter(p=>p.status==='pending').length>0 && <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F6B24A] px-1 text-[10px] font-bold text-[#1A1208]">{payments.filter(p=>p.status==='pending').length}</span>}</TabsTrigger>
            <TabsTrigger value="claims" data-testid="admin-tab-claims"><Building2 className="mr-2 h-4 w-4" /> Claims {claims.filter(c=>c.status==='pending').length>0 && <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F6B24A] px-1 text-[10px] font-bold text-[#1A1208]">{claims.filter(c=>c.status==='pending').length}</span>}</TabsTrigger>
            <TabsTrigger value="resets" data-testid="admin-tab-resets"><KeyRound className="mr-2 h-4 w-4" /> Resets {resets.filter(r=>r.status==='pending').length>0 && <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F6B24A] px-1 text-[10px] font-bold text-[#1A1208]">{resets.filter(r=>r.status==='pending').length}</span>}</TabsTrigger>
            <TabsTrigger value="cleanup" data-testid="admin-tab-cleanup"><Sparkles className="mr-2 h-4 w-4" /> Cleanup</TabsTrigger>
          </TabsList>

          <TabsContent value="stats" className="space-y-3 mt-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <GlassCard className="p-3">
                <p className="text-2xl font-bold text-white" data-testid="admin-stat-users">{stats?.users ?? '-'}</p>
                <p className="text-xs uppercase tracking-wider text-white/60">Users</p>
              </GlassCard>
              <GlassCard className="p-3">
                <p className="text-2xl font-bold text-white" data-testid="admin-stat-venues">{stats?.venues ?? '-'}</p>
                <p className="text-xs uppercase tracking-wider text-white/60">Venues</p>
              </GlassCard>
              <GlassCard className="p-3">
                <p className="text-2xl font-bold text-white" data-testid="admin-stat-posts">{stats?.posts ?? '-'}</p>
                <p className="text-xs uppercase tracking-wider text-white/60">Posts</p>
              </GlassCard>
              <GlassCard className="p-3">
                <p className="text-2xl font-bold text-white" data-testid="admin-stat-checkins">{stats?.checkins ?? '-'}</p>
                <p className="text-xs uppercase tracking-wider text-white/60">Check-ins</p>
              </GlassCard>
            </div>
            <GlassCard className="p-4">
              <p className="font-display text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Venues by category</p>
              <div className="h-60 mt-3">
                <ResponsiveContainer>
                  <BarChart data={catChartData}>
                    <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                    <XAxis dataKey="name" stroke="#b8c0cc" fontSize={11} />
                    <YAxis stroke="#b8c0cc" fontSize={11} allowDecimals={false} />
                    <RTooltip contentStyle={{ background: '#0B0C10', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12 }} />
                    <Bar dataKey="count" fill="#F6B24A" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>
            <GlassCard className="p-4">
              <p className="font-display text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Top venues by check-ins</p>
              <ul className="mt-3 space-y-2">
                {stats?.top_venues?.length ? stats.top_venues.map((t, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 rounded-lg bg-white/5 p-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{t.venue?.name || 'Unknown'}</p>
                      <p className="text-xs text-white/60">{t.venue?.city}, {t.venue?.state}</p>
                    </div>
                    <span className="text-sm font-mono text-[#F6B24A]">{t.checkins} check-ins</span>
                  </li>
                )) : <p className="text-sm text-white/60">No check-ins yet.</p>}
              </ul>
            </GlassCard>
            <GlassCard className="p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="font-display text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Daily Auto-Seeder</p>
                  <p className="text-xs text-white/70">Posts & stories from fictitious users until you have <strong>{seeder?.real_user_threshold ?? 50}+ real users</strong>.</p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${
                  seeder?.auto_seed_enabled
                    ? 'bg-green-500/15 text-green-300 border-green-500/30'
                    : 'bg-white/10 text-white/60 border-white/15'
                }`} data-testid="admin-seeder-status">{seeder?.auto_seed_enabled ? 'On' : 'Paused'}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white/5 p-2">
                  <p className="text-lg font-bold text-white" data-testid="seeder-real-users">{seeder?.real_users ?? '-'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/60">Real users</p>
                </div>
                <div className="rounded-lg bg-white/5 p-2">
                  <p className="text-lg font-bold text-white" data-testid="seeder-fictitious-posts">{seeder?.fictitious_posts ?? '-'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/60">Bot posts</p>
                </div>
                <div className="rounded-lg bg-white/5 p-2">
                  <p className="text-lg font-bold text-white" data-testid="seeder-fictitious-stories">{seeder?.fictitious_stories ?? '-'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/60">Bot stories</p>
                </div>
              </div>
              {seeder?.last_run && (
                <p className="mt-2 text-[11px] text-white/60">
                  Last run: {seeder.last_run.value} · {seeder.last_run.posts_created || 0} posts, {seeder.last_run.stories_created || 0} stories
                </p>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                <Button onClick={runSeeder} disabled={seederBusy} className="rounded-lg bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="admin-seeder-run">
                  {seederBusy ? 'Working…' : 'Generate Today\u2019s Content Now'}
                </Button>
                <Button onClick={cleanupSeeder} disabled={seederBusy} variant="outline" className="rounded-lg border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20" data-testid="admin-seeder-cleanup">
                  Cleanup Fictitious Data
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          <TabsContent value="venues" className="space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-white/70">{venues.length} venues total</p>
              <Button onClick={startCreate} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid="admin-venue-create-button">
                <Plus className="mr-2 h-4 w-4" /> Add Venue
              </Button>
            </div>
            <GlassCard className="divide-y divide-white/5">
              {venues.map((v) => (
                <div key={v.id} className="flex items-center gap-3 p-3" data-testid={`admin-venue-row-${v.id}`}>
                  <img
                    src={v.image_url}
                    onError={(e) => { e.currentTarget.src = '/logo-hero.png'; }}
                    className="h-12 w-16 rounded-md object-cover"
                    alt=""
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{v.name}</p>
                    <p className="text-xs text-white/60 truncate">{CATEGORY_META[v.category]?.label} · {v.city}, {v.state}</p>
                  </div>
                  <Button onClick={() => startEdit(v)} size="icon" variant="ghost" className="text-white/80 hover:bg-white/10" data-testid={`admin-venue-edit-${v.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button onClick={() => remove(v)} size="icon" variant="ghost" className="text-red-300 hover:bg-red-500/10" data-testid={`admin-venue-delete-${v.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </GlassCard>
          </TabsContent>

          <TabsContent value="payments" className="space-y-3 mt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-white/70">{payments.filter(p => p.status === 'pending').length} pending • {payments.length} total</p>
              <div className="flex gap-1.5" data-testid="admin-payment-filter">
                {['all', 'pending', 'approved', 'rejected'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setPayFilter(f)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize transition-colors ${
                      payFilter === f
                        ? 'border-[#F6B24A]/60 bg-[#F6B24A]/15 text-[#F6B24A]'
                        : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                    data-testid={`admin-payment-filter-${f}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <GlassCard className="divide-y divide-white/5">
              {payments.filter((p) => payFilter === 'all' || p.status === payFilter).length === 0 && (
                <p className="p-4 text-center text-white/60 text-sm" data-testid="admin-payments-empty">
                  No {payFilter === 'all' ? '' : `${payFilter} `}payment submissions.
                </p>
              )}
              {payments.filter((p) => payFilter === 'all' || p.status === payFilter).map((p) => (
                <div key={p.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center" data-testid={`admin-payment-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-white">${p.amount_usd.toLocaleString()} • <span className="capitalize">{p.plan}</span></p>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        p.status === 'pending' ? 'bg-white/10 text-white/70 border-white/15'
                        : p.status === 'approved' ? 'bg-green-500/15 text-green-300 border-green-500/30'
                        : 'bg-red-500/15 text-red-300 border-red-500/30'
                      }`} data-testid={`admin-payment-status-${p.id}`}>{p.status}</span>
                    </div>
                    <p className="text-xs text-white/60 truncate">{p.venue_name} • From {p.submitted_by_user_name} ({p.submitted_by_user_email})</p>
                    <p className="text-xs text-white/50 font-mono">Conf #{p.zelle_confirmation} • Sender: {p.zelle_sender_name}</p>
                  </div>
                  {p.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button onClick={() => approvePayment(p.id)} className="rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 hover:bg-green-500/30" variant="outline" data-testid={`admin-payment-approve-${p.id}`}>
                        <Check className="mr-1 h-4 w-4" /> Approve
                      </Button>
                      <Button onClick={() => rejectPayment(p.id)} className="rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25" variant="outline" data-testid={`admin-payment-reject-${p.id}`}>
                        <X className="mr-1 h-4 w-4" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </GlassCard>

            <div className="pt-2">
              <p className="text-sm font-semibold text-white">Active Subscriptions <span className="text-white/50 font-normal">({subs.length})</span></p>
              <GlassCard className="mt-2 divide-y divide-white/5" data-testid="admin-active-subscriptions">
                {subs.length === 0 && <p className="p-4 text-center text-white/60 text-sm" data-testid="admin-subscriptions-empty">No active paid subscriptions.</p>}
                {subs.map((sub) => (
                  <div key={sub.id} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-subscription-row-${sub.id}`}>
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm font-semibold text-white">
                        <span className="truncate">{sub.venue_name}</span>
                        <span className="rounded-full border border-[#F6B24A]/40 bg-[#F6B24A]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#F6B24A]">{sub.plan}</span>
                      </p>
                      <p className="truncate text-xs text-white/60">{sub.owner_email || 'No owner email on file'}{sub.owner_name ? ` (${sub.owner_name})` : ''}</p>
                    </div>
                    <p className="shrink-0 text-xs text-white/50">
                      {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : '—'} → <span className="text-white/80">{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}</span>
                    </p>
                  </div>
                ))}
              </GlassCard>
            </div>
          </TabsContent>

          <TabsContent value="claims" className="space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-white/70">{claims.filter(c => c.status === 'pending').length} pending • {claims.length} total</p>
            </div>
            <GlassCard className="divide-y divide-white/5">
              {claims.length === 0 && <p className="p-4 text-center text-white/60 text-sm">No venue claim requests yet.</p>}
              {claims.map((c) => (
                <div key={c.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center" data-testid={`admin-claim-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-white truncate">{c.venue_name}</p>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        c.status === 'pending' ? 'bg-white/10 text-white/70 border-white/15'
                        : c.status === 'approved' ? 'bg-green-500/15 text-green-300 border-green-500/30'
                        : 'bg-red-500/15 text-red-300 border-red-500/30'
                      }`}>{c.status}</span>
                    </div>
                    <p className="text-xs text-white/60 truncate">{c.full_name} ({c.role_at_venue}) • {c.user_email} • {c.contact_phone}</p>
                    {c.proof_notes && <p className="text-xs text-white/50 truncate">Proof: {c.proof_notes}</p>}
                  </div>
                  {c.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button onClick={() => approveClaim(c.id)} className="rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 hover:bg-green-500/30" variant="outline" data-testid={`admin-claim-approve-${c.id}`}>
                        <Check className="mr-1 h-4 w-4" /> Approve
                      </Button>
                      <Button onClick={() => rejectClaim(c.id)} className="rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25" variant="outline" data-testid={`admin-claim-reject-${c.id}`}>
                        <X className="mr-1 h-4 w-4" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </GlassCard>
          </TabsContent>

          <TabsContent value="resets" className="space-y-3 mt-3">
            <GlassCard className="p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <p className="font-display text-white font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Password reset queue</p>
                  <p className="text-xs text-white/55">Users who clicked &ldquo;Forgot password?&rdquo;. Generate a one-time link (15-min expiry) and share it with them via your own channel.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={loadAll} className="text-white/80 hover:bg-white/10" data-testid="admin-resets-refresh-button">Refresh</Button>
              </div>
              {resets.length === 0 && (
                <p className="text-sm text-white/55 py-6 text-center" data-testid="admin-resets-empty">No password reset requests yet.</p>
              )}
              {resets.map((r) => {
                const link = linksByReq[r.id];
                const ready = r.status === 'ready';
                const expired = ready && r.token_expires_at && new Date(r.token_expires_at) < new Date();
                return (
                  <div key={r.id} className="rounded-xl border border-white/10 bg-white/5 p-3 mb-2" data-testid={`admin-reset-row-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate" data-testid={`admin-reset-email-${r.id}`}>{r.email}</p>
                        <p className="text-xs text-white/55">{r.display_name || '—'} · requested {new Date(r.requested_at).toLocaleString()}</p>
                        <p className="mt-1 text-[11px] uppercase tracking-wider">
                          <span className={
                            r.status === 'pending' ? 'text-[#F6B24A]' :
                            r.status === 'ready' && !expired ? 'text-emerald-300' :
                            r.status === 'used' ? 'text-white/55' :
                            'text-red-300'
                          } data-testid={`admin-reset-status-${r.id}`}>
                            {expired ? 'expired' : r.status}
                          </span>
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        {r.status === 'pending' && (
                          <Button size="sm" onClick={() => generateResetLink(r.id)} className="bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid={`admin-reset-generate-${r.id}`}>
                            <KeyRound className="mr-1 h-3.5 w-3.5" /> Generate link
                          </Button>
                        )}
                        {(r.status === 'pending' || (ready && !expired)) && (
                          <Button size="sm" variant="outline" onClick={() => cancelReset(r.id)} className="border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid={`admin-reset-cancel-${r.id}`}>
                            <X className="mr-1 h-3.5 w-3.5" /> Cancel
                          </Button>
                        )}
                      </div>
                    </div>

                    {link && (
                      <div className="mt-3 rounded-lg bg-black/40 border border-emerald-400/25 p-2.5">
                        <p className="text-[11px] uppercase tracking-wider text-emerald-300 mb-1.5">Reset link (share with the user — expires in 15 min)</p>
                        <div className="flex items-center gap-2">
                          <Input value={link.url} readOnly onFocus={(e) => e.target.select()} className="bg-white/5 border-white/15 text-white text-xs font-mono" data-testid={`admin-reset-link-input-${r.id}`} />
                          <Button size="sm" variant="outline" onClick={() => copyResetLink(link.url)} className="shrink-0 border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid={`admin-reset-copy-${r.id}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <a href={`sms:?body=${encodeURIComponent('Reset your Thirrsty password: ' + link.url)}`} className="text-xs text-[#2DE2E6] hover:underline">Text</a>
                          <span className="text-white/30">·</span>
                          <a href={`mailto:${r.email}?subject=${encodeURIComponent('Reset your Thirrsty password')}&body=${encodeURIComponent('Hi ' + (r.display_name || '') + ',\n\nUse this link to reset your password (valid 15 min):\n\n' + link.url + '\n\n— Thirrsty')}`} className="text-xs text-[#F6B24A] hover:underline">Email</a>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </GlassCard>
          </TabsContent>

          <TabsContent value="cleanup" className="space-y-3 mt-3">
            <InactiveUserCleanupPanel />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[#0B0C10] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="admin-venue-dialog">
          <DialogHeader><DialogTitle className="text-white">{editing ? 'Edit Venue' : 'Add Venue'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/12 text-white" data-testid="admin-venue-form-name" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-white/5 border-white/12 text-white" data-testid="admin-venue-form-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                  {CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{CATEGORY_META[c].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/12 text-white" />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <AddressAutocompleteInput
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                onSelect={(d) => setForm((f) => ({
                  ...f,
                  address: d.address || f.address,
                  city: d.city || f.city,
                  state: d.state || f.state,
                  lat: d.lat ?? f.lat,
                  lon: d.lon ?? f.lon,
                }))}
                className="bg-white/5 border-white/12 text-white"
                testId="admin-venue-form-address"
              />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white/5 border-white/12 text-white" />
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-white/5 border-white/12 text-white" />
            </div>
            <div>
              <Label>Latitude</Label>
              <Input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="bg-white/5 border-white/12 text-white" />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input type="number" step="0.0001" value={form.lon} onChange={(e) => setForm({ ...form, lon: e.target.value })} className="bg-white/5 border-white/12 text-white" />
            </div>
            <div className="col-span-2">
              <Label>Hours</Label>
              <Input value={form.hours || ''} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="bg-white/5 border-white/12 text-white" />
            </div>
            <div className="col-span-2">
              <Label>Image URL</Label>
              <Input value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-white/5 border-white/12 text-white" placeholder="https://images.unsplash.com/..." />
            </div>
            <div className="col-span-2">
              <Label>Discount text</Label>
              <Input value={form.discount_text || ''} onChange={(e) => setForm({ ...form, discount_text: e.target.value })} className="bg-white/5 border-white/12 text-white" placeholder="15% off all drinks with Thirrsty QR" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/12 text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="admin-venue-form-save">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
